import { Small, Icon } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

import Icons from "src/config/icons";
import ExpandButton from "./ExpandButton";

const Container = styled.div`
  position: relative;
  max-height: ${(props) =>
    props.isCollapsed ? `${props.maxHeight}px` : "none"};
  overflow: hidden;
  padding-bottom: ${(props) => {
    if (props.noCollapse && !props.hasFooter) {
      return "0px";
    }
    // Only add padding-bottom when footer is absolute (collapsed state)
    // When expanded, footer is static and has its own padding-top
    // Reduce padding when footerContent exists to prevent overlap
    if (props.hasFooter && !props.isAbsoluteFooter) {
      return "0px";
    }
    return props.isAbsoluteFooter ? "20px" : "0px";
  }};
  transition: max-height 0.2s ease-in-out;

  &::after {
    content: "";
    display: ${(props) =>
      props.isCollapsed && !props.noCollapse ? "block" : "none"};
    position: absolute;
    bottom: 0;
    left: 0;
    height: 100px;
    width: 100%;
    background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 10%,
      rgba(255, 255, 255, 1) 80%
    );
    pointer-events: none;
  }
`;

const StyledExpandButton = styled(ExpandButton)`
  align-items: center;
`;

const FooterRow = styled.div`
  position: ${(props) => (props.isAbsolute ? "absolute" : "static")};
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  z-index: 1;
  padding-top: ${(props) => (props.isAbsolute ? "0" : "8px")};
  padding-bottom: 0;
  background: ${(props) =>
    props.isAbsolute
      ? "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))"
      : "transparent"};
`;

const FooterContentWrapper = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
`;

const ExpandableContent = ({
  children,
  maxHeight = 150,
  initialExpanded = false,
  footerContent = null,
}) => {
  const [isCollapsed, setCollapsed] = useState(!initialExpanded);
  const [noCollapse, setNoCollapse] = useState(false);
  const containerRef = useRef();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    setNoCollapse(el.scrollHeight <= maxHeight);
  }, [maxHeight, children]);

  const onClick = () => {
    setCollapsed(!isCollapsed);
  };

  const title = isCollapsed ? "Show more" : "Show less";

  const shouldRenderFooter = !noCollapse || footerContent;
  const isAbsoluteFooter = !noCollapse && isCollapsed;

  return (
    <Container
      ref={containerRef}
      isCollapsed={isCollapsed}
      maxHeight={maxHeight}
      noCollapse={noCollapse}
      hasFooter={Boolean(footerContent)}
      isAbsoluteFooter={isAbsoluteFooter}
    >
      {children}
      {shouldRenderFooter && (
        <FooterRow isAbsolute={isAbsoluteFooter}>
          {!noCollapse && (
            <StyledExpandButton
              aria-label={`${title} - visual effect only`}
              onClick={onClick}
              isCollapsed={isCollapsed}
            >
              <Small mr="xs">{title}</Small>
              <Icon icon={isCollapsed ? Icons.chevronDown : Icons.chevronUp} />
            </StyledExpandButton>
          )}
          {footerContent && (
            <FooterContentWrapper>{footerContent}</FooterContentWrapper>
          )}
        </FooterRow>
      )}
    </Container>
  );
};

ExpandableContent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  maxHeight: PropTypes.number,
  initialExpanded: PropTypes.bool,
  footerContent: PropTypes.node,
};

export default ExpandableContent;
